import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Truck, DollarSign, MapPin, CheckCircle2, ArrowRight, Star, Route, TrendingUp, Package
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../context/AuthContext";
import { subscribeDriverStops, STOP_STATUS_META } from "../lib/deliveries";

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.toDateString() === n.toDateString();
}
function isThisWeek(iso) {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  const start = new Date(n);
  start.setDate(n.getDate() - n.getDay());
  start.setHours(0, 0, 0, 0);
  return d >= start;
}

export default function DriverDashboard() {
  const { user } = useAuth();
  const [online, setOnline] = useState(true);
  const [stops, setStops] = useState([]);

  // Stops scoped to THIS authenticated driver only (server-constrained query).
  useEffect(() => {
    if (!user) return;
    const unsub = subscribeDriverStops(user.email, setStops);
    return () => unsub && unsub();
  }, [user]);

  const metrics = useMemo(() => {
    const delivered = stops.filter((s) => s.status === "delivered");
    const todayStops = delivered.filter((s) => isToday(s.delivered_time)).length;
    const weekStops = delivered.filter((s) => isThisWeek(s.delivered_time)).length;
    const hasHistory = delivered.length > 0;
    return { todayStops, weekStops, hasHistory, deliveredTotal: delivered.length };
  }, [stops]);

  // Real data only. Earnings & mileage are not recorded per-stop yet → $0 / 0.
  // On-time & rating require data that doesn't exist yet → N/A.
  const stats = [
    { label: "Stops today", value: String(metrics.todayStops), icon: MapPin, color: "#0f2847" },
    { label: "Miles driven", value: "0", icon: Route, color: "#6366f1" },
    { label: "Earnings today", value: "$0.00", icon: DollarSign, color: "#10b981" },
    { label: "On-time rate", value: "N/A", icon: TrendingUp, color: "#f59321" }
  ];

  // Today's routes = stops from assigned jobs. Empty for a brand-new driver.
  const routeStops = useMemo(
    () => [...stops].sort((a, b) => (a.job_id + String(a.stop_number).padStart(3, "0")).localeCompare(b.job_id + String(b.stop_number).padStart(3, "0"))),
    [stops]
  );
  const pending = routeStops.filter((s) => s.status !== "delivered").length;

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-white border-b border-slate-200">
        <div className="container-518 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-1">Driver portal</div>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0f2847]">Hi, {user?.name || "driver"}.</h1>
              <p className="mt-1 text-slate-600" data-testid="driver-subtitle">
                {routeStops.length === 0
                  ? "No deliveries assigned yet."
                  : <>You have <span className="font-bold text-[#0f2847]">{pending} stop{pending === 1 ? "" : "s"}</span> remaining.</>}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/driver/deliveries">
                <Button className="rounded-full h-11 px-4 font-semibold bg-[#f59321] hover:bg-[#e07f10] text-white" data-testid="driver-deliveries-link">
                  <Truck className="w-4 h-4 mr-1.5" /> My routes
                </Button>
              </Link>
              <Link to="/compliance-center">
                <Button variant="outline" className="rounded-full h-11 px-4 font-semibold border-2" data-testid="driver-compliance-link">
                  <MapPin className="w-4 h-4 mr-1.5" /> Compliance docs
                </Button>
              </Link>
              <div className={`flex items-center gap-3 px-4 py-2.5 rounded-full border-2 ${online ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-100"}`}>
                <span className="relative flex w-2.5 h-2.5">
                  {online && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${online ? "bg-emerald-500" : "bg-slate-400"}`} />
                </span>
                <span className={`text-sm font-bold ${online ? "text-emerald-700" : "text-slate-500"}`}>{online ? "Online" : "Offline"}</span>
                <Switch checked={online} onCheckedChange={setOnline} className="data-[state=checked]:bg-emerald-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-518 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="driver-stats">
          {stats.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-slate-200 card-lift">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white mb-3" style={{ background: s.color }}>
                <s.icon className="w-5 h-5" />
              </div>
              <div className="font-display text-3xl font-extrabold text-[#0f2847]" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s.value}</div>
              <div className="mt-1 text-sm text-slate-500 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Routes */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display font-bold text-[#0f2847]">Today's routes</h3>
              <span className="text-xs font-bold text-slate-500">{routeStops.length} stop{routeStops.length === 1 ? "" : "s"}</span>
            </div>
            {routeStops.length === 0 ? (
              <div className="px-6 py-16 text-center" data-testid="driver-routes-empty">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No deliveries assigned today.</p>
                <p className="text-sm text-slate-400 mt-1">Assigned routes from dispatch will appear here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {routeStops.map((r) => {
                  const m = STOP_STATUS_META[r.status] || STOP_STATUS_META.pending;
                  return (
                    <li key={r.id} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50">
                      <div className="w-10 text-center font-display font-extrabold text-slate-400">{String(r.stop_number).padStart(2, "0")}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[#0f2847] text-sm truncate">{r.recipient_name}{r.company_name ? ` · ${r.company_name}` : ""}</div>
                        <div className="text-xs text-slate-500 truncate">{r.address_line_1}, {r.city} · {r.package_reference}</div>
                      </div>
                      <span className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>
                      {r.status === "delivered" ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                      ) : (
                        <Link to={`/driver/deliveries/${r.job_id}/stop/${r.id}`}>
                          <Button size="sm" className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-9 px-3"><ArrowRight className="w-3.5 h-3.5" /></Button>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Side */}
          <div className="space-y-5">
            <div className="bg-[#0f2847] text-white rounded-2xl p-6 stripe-bg">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-2">This week</div>
              <div className="font-display text-4xl font-extrabold" data-testid="week-earnings">$0.00</div>
              <div className="text-sm text-slate-300 mt-1">in earnings · <span data-testid="week-stops">{metrics.weekStops}</span> stops</div>
              <div className="mt-5 h-2 bg-white/15 rounded-full overflow-hidden">
                <div className="h-full bg-[#f59321] rounded-full" style={{ width: metrics.weekStops > 0 ? "100%" : "0%" }} />
              </div>
              <div className="mt-2 text-xs text-slate-300">Earnings tracking coming soon · pay-out every Friday</div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center">
                  <Star className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-display text-2xl font-extrabold text-[#0f2847]" data-testid="driver-rating">{metrics.hasHistory ? "N/A" : "No ratings yet"}</div>
                  <div className="text-xs text-slate-500">Avg rating</div>
                </div>
              </div>
              <div className="text-sm text-slate-600 leading-relaxed">
                {metrics.hasHistory ? "Ratings appear once customers or admins review your deliveries." : "Complete deliveries to start building your rating."}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
